import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { NewSideBarMagasins } from "../../NewSidebarMagasin";
import axios from "axios";

/* ─────────────────────────────────────────────
   Types
───────────────────────────────────────────── */
type Ticket = {
  id: number;
  NamUtilisateur: string;
  Probleme: string;
  Description: string;
  Status: string;
  Date: string;
};

/* ─────────────────────────────────────────────
   Status config
───────────────────────────────────────────── */
type StatusKey = "Ouvert" | "En attente" | "Résolu" | "Fermé" | "Non lu" | "Lu";

const STATUS_CFG: Record<StatusKey, { rail: string; dot: string; bg: string; text: string; label: string }> = {
  "Non lu":     { rail: "#F59E0B", dot: "#F59E0B", bg: "#FEF3C7", text: "#92400E", label: "Non lu" },
  "Lu":         { rail: "#6366F1", dot: "#6366F1", bg: "#EEF2FF", text: "#3730A3", label: "Lu" },
  "Résolu":     { rail: "#10B981", dot: "#10B981", bg: "#D1FAE5", text: "#065F46", label: "Résolu" },
  "Ouvert":     { rail: "#3B82F6", dot: "#3B82F6", bg: "#DBEAFE", text: "#1E40AF", label: "Ouvert" },
  "En attente": { rail: "#F97316", dot: "#F97316", bg: "#FFEDD5", text: "#9A3412", label: "En attente" },
  "Fermé":      { rail: "#94A3B8", dot: "#94A3B8", bg: "#F1F5F9", text: "#475569", label: "Fermé" },
};

function getCfg(s: string) {
  return STATUS_CFG[s as StatusKey] ?? STATUS_CFG["Fermé"];
}

/* ─────────────────────────────────────────────
   Embedded CSS — no external file
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

.at-root *, .at-root *::before, .at-root *::after {
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  margin: 0; padding: 0;
}

.at-root {
  --navy:   #0B0F20;
  --navy2:  #151B30;
  --gold:   #F59E0B;
  --surface:#F7F8FC;
  --card:   #FFFFFF;
  --border: #E8ECF4;
  --text:   #0F172A;
  --sub:    #64748B;
  --muted:  #94A3B8;
  background: var(--surface);
  min-height: 100vh;
  color: var(--text);
}

/* ── Page shell ── */
.at-page { max-width: 980px; margin: 0 auto; padding: 36px 24px 80px; }

/* ── Page header ── */
.at-header {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 20px; margin-bottom: 28px;
  animation: at-up .38s ease both;
  flex-wrap: wrap;
}
.at-header-left h1 {
  font-size: 1.55rem; font-weight: 900; color: var(--text); letter-spacing: -.5px;
}
.at-header-left p { font-size: .82rem; color: var(--muted); margin-top: 4px; }

/* ── Search ── */
.at-search {
  display: flex; align-items: center; gap: 10px;
  background: var(--card); border: 1.5px solid var(--border);
  border-radius: 12px; padding: 9px 16px;
  width: 280px; transition: border-color .18s, box-shadow .18s;
  flex-shrink: 0;
}
.at-search:focus-within {
  border-color: #CBD5E1;
  box-shadow: 0 0 0 3px rgba(99,102,241,.08);
}
.at-search input {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: .875rem; color: var(--text);
}
.at-search input::placeholder { color: var(--muted); }

/* ── KPI strip ── */
.at-kpi-row {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 11px;
  margin-bottom: 28px;
}
@media (max-width: 640px) { .at-kpi-row { grid-template-columns: repeat(2, 1fr); } }
.at-kpi {
  background: var(--card); border: 1.5px solid var(--border);
  border-radius: 13px; padding: 15px 17px; position: relative; overflow: hidden;
  animation: at-up .38s ease both;
  transition: transform .18s, box-shadow .18s;
}
.at-kpi:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(11,15,32,.08); }
.at-kpi-bar { position: absolute; top: 0; left: 0; right: 0; height: 3px; border-radius: 13px 13px 0 0; }
.at-kpi-lbl { font-size: .67rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .7px; margin-top: 6px; }
.at-kpi-val { font-size: 1.8rem; font-weight: 900; color: var(--text); line-height: 1; margin-top: 5px; }

/* ── Toolbar ── */
.at-toolbar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-bottom: 16px; flex-wrap: wrap;
  animation: at-up .38s .07s ease both;
}
.at-tabs { display: flex; gap: 4px; flex-wrap: wrap; }
.at-tab {
  padding: 6px 14px; border-radius: 8px; font-size: .775rem; font-weight: 600;
  cursor: pointer; border: 1.5px solid transparent; background: transparent;
  color: var(--sub); transition: all .15s; display: inline-flex; align-items: center; gap: 6px;
}
.at-tab:hover  { background: var(--card); border-color: var(--border); color: var(--text); }
.at-tab.active { background: var(--navy); color: #fff; border-color: var(--navy); }
.at-tab-badge {
  font-size: .67rem; font-weight: 700; padding: 1px 6px; border-radius: 20px;
  background: rgba(255,255,255,.18); color: #fff;
}
.at-tab:not(.active) .at-tab-badge { background: var(--border); color: var(--sub); }
.at-result-count { font-size: .8rem; color: var(--muted); }

/* ── List ── */
.at-list { display: flex; flex-direction: column; gap: 8px; }

/* ── Card ── */
.at-card {
  background: var(--card); border: 1.5px solid var(--border); border-radius: 14px;
  display: flex; align-items: stretch; overflow: hidden;
  transition: transform .18s, box-shadow .18s, border-color .18s;
  animation: at-up .35s ease both;
}
.at-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(11,15,32,.09);
  border-color: #CBD5E1;
}

/* Color rail */
.at-rail { width: 5px; flex-shrink: 0; }

/* Card body */
.at-card-body {
  flex: 1; display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px; gap: 16px; min-width: 0;
}

/* Left info */
.at-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.at-info-top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.at-id-pill {
  font-size: .7rem; font-weight: 800; color: var(--muted);
  background: var(--surface); padding: 2px 8px; border-radius: 6px;
  font-variant-numeric: tabular-nums; letter-spacing: .3px; flex-shrink: 0;
}
.at-status-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px; border-radius: 20px;
  font-size: .7rem; font-weight: 700;
}
.at-status-dot { width: 6px; height: 6px; border-radius: 50%; }

.at-problem {
  font-size: .9rem; font-weight: 700; color: var(--text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.at-meta { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.at-meta-item { font-size: .73rem; color: var(--muted); display: flex; align-items: center; gap: 4px; }

/* Right action */
.at-action { flex-shrink: 0; }
.at-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; border-radius: 10px; border: none;
  background: var(--navy); color: #fff;
  font-size: .8rem; font-weight: 700; cursor: pointer;
  transition: opacity .18s, transform .18s;
  text-decoration: none; white-space: nowrap;
}
.at-btn:hover { opacity: .88; transform: translateY(-1px); }

/* ── Empty state ── */
.at-empty {
  background: var(--card); border: 1.5px solid var(--border);
  border-radius: 16px; padding: 72px 30px; text-align: center;
  animation: at-up .38s ease both;
}
.at-empty-icon {
  width: 52px; height: 52px; border-radius: 14px;
  background: var(--surface); border: 1.5px solid var(--border);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 18px;
}
.at-empty h3 { font-size: 1rem; font-weight: 700; color: var(--text); margin-bottom: 6px; }
.at-empty p  { font-size: .85rem; color: var(--muted); }

/* ── Skeleton loader ── */
.at-skeleton-list { display: flex; flex-direction: column; gap: 8px; }
.at-skeleton-card {
  background: var(--card); border: 1.5px solid var(--border);
  border-radius: 14px; height: 78px; overflow: hidden; position: relative;
}
.at-skeleton-card::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.6) 50%, transparent 100%);
  animation: at-shimmer 1.4s ease infinite;
}
@keyframes at-shimmer {
  from { transform: translateX(-100%); }
  to   { transform: translateX(100%); }
}

/* ── Keyframes ── */
@keyframes at-up { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }

/* ── Responsive ── */
@media (max-width: 560px) {
  .at-header { flex-direction: column; align-items: flex-start; }
  .at-search  { width: 100%; }
  .at-card-body { flex-direction: column; align-items: flex-start; }
  .at-btn  { width: 100%; justify-content: center; }
}
`;

/* ─────────────────────────────────────────────
   Skeleton loader
───────────────────────────────────────────── */
function SkeletonList() {
  return (
    <div className="at-skeleton-list">
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className="at-skeleton-card"
          style={{
            background: `linear-gradient(90deg, #F1F5F9 0%, #E8ECF4 50%, #F1F5F9 100%)`,
            animationDelay: `${i * 80}ms`,
          }}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Ticket Card
───────────────────────────────────────────── */
function TicketCard({ ticket, delay }: { ticket: Ticket; delay: number }) {
  const cfg = getCfg(ticket.Status);
  const date = ticket.Date
    ? new Date(ticket.Date).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "—";

  return (
    <div className="at-card" style={{ animationDelay: `${delay}ms` }}>
      {/* Color rail */}
      <div className="at-rail" style={{ background: cfg.rail }} />

      {/* Body */}
      <div className="at-card-body">
        <div className="at-info">
          <div className="at-info-top">
            <span className="at-id-pill">TK-{ticket.id}</span>
            <span
              className="at-status-pill"
              style={{ background: cfg.bg, color: cfg.text }}
            >
              <span className="at-status-dot" style={{ background: cfg.dot }} />
              {cfg.label}
            </span>
          </div>

          <div className="at-problem">
            {ticket.Probleme}
            {ticket.NamUtilisateur && (
              <span style={{ fontWeight: 500, color: "#64748B" }}>
                {" — "}{ticket.NamUtilisateur}
              </span>
            )}
          </div>

          <div className="at-meta">
            <span className="at-meta-item">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8"  y1="2" x2="8"  y2="6"/>
                <line x1="3"  y1="10" x2="21" y2="10"/>
              </svg>
              {date}
            </span>
          </div>
        </div>

        <div className="at-action">
          <Link to={`/magasin/ticket/conversation/${ticket.id}`} style={{ textDecoration: "none" }}>
            <button className="at-btn">
              Consulter
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
export default function AllTickets() {
  const [tickets, setTickets]   = useState<Ticket[]>([]);
  const [search, setSearch]     = useState("");
  const [filter, setFilter]     = useState("all");
  const [loading, setLoading]   = useState(true);
  const magasinId = localStorage.getItem("MagasinId");

  useEffect(() => {
    if (!magasinId) { setLoading(false); return; }
    axios
      .get(`${process.env.REACT_APP_PHP_APP_URL}/magasins/${magasinId}/tickets`)
      .then(res => setTickets(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [magasinId]);

  /* ── Counts ── */
  const counts: Record<string, number> = {};
  tickets.forEach(t => { counts[t.Status] = (counts[t.Status] || 0) + 1; });
  const openCount   = Object.entries(counts)
    .filter(([k]) => !["Résolu", "Fermé"].includes(k))
    .reduce((s, [, v]) => s + v, 0);
  const resolvedCount = counts["Résolu"] || 0;

  /* ── Filter + search ── */
  const displayed = tickets.filter(t => {
    const matchFilter = filter === "all" || t.Status === filter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (t.Probleme       && t.Probleme.toLowerCase().includes(q)) ||
      (t.NamUtilisateur && t.NamUtilisateur.toLowerCase().includes(q)) ||
      t.id.toString().includes(q);
    return matchFilter && matchSearch;
  });

  /* ── Status filter tabs ── */
  const statusTabs = [
    { key: "all",    label: "Tous",    count: tickets.length },
    { key: "Non lu", label: "Non lu",  count: counts["Non lu"] || 0 },
    { key: "Lu",     label: "Lu",      count: counts["Lu"] || 0 },
    { key: "Résolu", label: "Résolu",  count: counts["Résolu"] || 0 },
    { key: "Fermé",  label: "Fermé",   count: counts["Fermé"] || 0 },
  ].filter(t => t.key === "all" || t.count > 0);

  /* ── KPI cards ── */
  const kpis = [
    { val: tickets.length, lbl: "Total",     accent: "#6366F1" },
    { val: openCount,      lbl: "En cours",  accent: "#F59E0B" },
    { val: resolvedCount,  lbl: "Résolus",   accent: "#10B981" },
    { val: counts["Fermé"] || 0, lbl: "Fermés", accent: "#94A3B8" },
  ];

  return (
    <>
      <NewSideBarMagasins />
      <div className="at-root">
        <style>{CSS}</style>

        <div className="at-page">

        {/* ── Header ── */}
        <div className="at-header">
          <div className="at-header-left">
            <h1>Tickets</h1>
            <p>Vue d'ensemble de tous les tickets d'assistance</p>
          </div>
          <div className="at-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="#94A3B8" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Sujet, client ou ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{ background: "none", border: "none", cursor: "pointer",
                  color: "#94A3B8", display: "flex", padding: "0" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6"  y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── KPI strip ── */}
        <div className="at-kpi-row">
          {kpis.map((k, i) => (
            <div className="at-kpi" key={k.lbl} style={{ animationDelay: `${i * 55}ms` }}>
              <div className="at-kpi-bar" style={{ background: k.accent }} />
              <div className="at-kpi-lbl">{k.lbl}</div>
              <div className="at-kpi-val">{k.val}</div>
            </div>
          ))}
        </div>

        {/* ── Toolbar ── */}
        <div className="at-toolbar">
          <div className="at-tabs">
            {statusTabs.map(tab => (
              <button
                key={tab.key}
                className={`at-tab${filter === tab.key ? " active" : ""}`}
                onClick={() => setFilter(tab.key)}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className="at-tab-badge">{tab.count}</span>
                )}
              </button>
            ))}
          </div>
          {!loading && (
            <span className="at-result-count">
              {displayed.length} résultat{displayed.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* ── Content ── */}
        {loading ? (
          <SkeletonList />
        ) : displayed.length === 0 ? (
          <div className="at-empty">
            <div className="at-empty-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="#94A3B8" strokeWidth="1.8" strokeLinecap="round">
                <path d="M20 12V22H4V12M22 7H2v5h20V7zM12 22V7
                  M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z
                  M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
              </svg>
            </div>
            <h3>
              {search
                ? `Aucun résultat pour "${search}"`
                : filter !== "all"
                  ? `Aucun ticket "${filter}"`
                  : "Aucun ticket"}
            </h3>
            <p>
              {search
                ? "Essayez un autre terme de recherche."
                : "Les tickets de vos clients apparaîtront ici."}
            </p>
          </div>
        ) : (
          <div className="at-list">
            {displayed.map((ticket, i) => (
              <TicketCard key={ticket.id} ticket={ticket} delay={i * 45} />
            ))}
          </div>
        )}

      </div>
    </div>
    </>
  );
}